import {
  ConfigApi,
  configApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import {
  ArtaFeatureFlags,
  resolveArtaCapabilities,
} from '@cubeangle/arta-common';

export type { ArtaEdition } from '@cubeangle/arta-common';
export type ArtaAuthProvider = 'microsoft' | 'okta';

function getFeatureFlags(configApi: ConfigApi): Partial<ArtaFeatureFlags> {
  return {
    templateManagement: configApi.getOptionalBoolean('app.artaFeatures.templateManagement'),
    templateAssistant: configApi.getOptionalBoolean('app.artaFeatures.templateAssistant'),
    techDocsGeneration: configApi.getOptionalBoolean('app.artaFeatures.techDocsGeneration'),
  } as Partial<ArtaFeatureFlags>;
}

export function getArtaCapabilities(configApi: ConfigApi) {
  return resolveArtaCapabilities({
    edition: configApi.getOptionalString('app.artaEdition'),
    licensedEdition: configApi.getOptionalString('app.artaLicenseEdition'),
    features: getFeatureFlags(configApi),
  });
}

export function getArtaEdition(configApi: ConfigApi) {
  return getArtaCapabilities(configApi).edition;
}

export function getArtaAuthProvider(configApi: ConfigApi): ArtaAuthProvider {
  const edition = getArtaEdition(configApi);
  const configured = configApi
    .getOptionalString('app.authProvider')
    ?.trim()
    .toLowerCase();
  if (configured === 'microsoft' || configured === 'entra') return 'microsoft';
  if (configured === 'okta') return 'okta';
  if (configured) {
    throw new Error(
      `Invalid app.authProvider "${configured}". Expected microsoft, entra, or okta.`,
    );
  }
  if (edition === 'multicloud') {
    throw new Error(
      'app.authProvider is required for the multicloud edition; select microsoft or okta.',
    );
  }
  return edition === 'aws' ? 'okta' : 'microsoft';
}

export function useArtaEdition() {
  return getArtaCapabilities(useApi(configApiRef));
}
